import { useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { PlusSignIcon, Delete02Icon } from "@hugeicons/core-free-icons";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDispatchCarSpecList, useDeleteDispatchCarSpec } from "../hooks/useCarSpecs";
import { CarSpecDialog } from "./CarSpecDialog";
import type { DispatchCarSpecItem } from "../types/carSpec.types";

interface CompanyCarSpecPanelProps {
  companyGuid: string;
}

export function CompanyCarSpecPanel({ companyGuid }: CompanyCarSpecPanelProps) {
  const { data: specs = [], isLoading } = useDispatchCarSpecList(companyGuid);
  const remove = useDeleteDispatchCarSpec(companyGuid);

  const [dialogState, setDialogState] = useState<
    { open: false } | { open: true; spec: DispatchCarSpecItem | null }
  >({ open: false });
  const [pendingDelete, setPendingDelete] = useState<DispatchCarSpecItem | null>(null);

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        <span className="text-xs text-gray-400">전용 톤수를 불러오는 중...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between px-4 py-2">
        <span className="text-xs text-gray-500">총 {specs.length}건</span>
        <Button
          size="sm"
          onClick={() => setDialogState({ open: true, spec: null })}
          className="h-7 bg-blue-600 px-2.5 text-xs font-bold hover:bg-blue-700"
        >
          <HugeiconsIcon icon={PlusSignIcon} size={12} className="mr-1" />
          톤수 추가
        </Button>
      </div>

      {/* 이 화면의 존재 이유 — 표준에 없는 톤수를 이 업체에만 열어주는 곳이다 */}
      <div className="mx-4 mb-2 shrink-0 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
        표준 톤수에 없는 톤수를 이 업체에만 추가합니다. 다른 업체에는 보이지 않으며, 정보망에 없는
        톤수라 해당 화물은 정보망 접수가 불가하고 직접 배차만 됩니다.
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-4">
        {specs.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-xs text-gray-400">
            등록된 전용 톤수가 없습니다
          </div>
        ) : (
          <table className="w-full">
            <thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="h-8 px-2 text-left text-xs font-medium text-gray-500">톤수</th>
                <th className="h-8 px-2 text-left text-xs font-medium text-gray-500">값</th>
                <th className="h-8 px-2 text-left text-xs font-medium text-gray-500">차종</th>
                <th className="h-8 w-8 px-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {specs.map((spec) => (
                <tr
                  key={spec.guid}
                  className="h-9 cursor-pointer hover:bg-gray-50"
                  onClick={() => setDialogState({ open: true, spec })}
                >
                  <td className="px-2 text-xs font-medium text-blue-600">{spec.carWeightStr}</td>
                  <td className="px-2 text-xs text-gray-700">{spec.carWeightValue}</td>
                  <td className="px-2 text-xs text-gray-700">
                    {spec.carTypes.length === 0
                      ? "-"
                      : spec.carTypes
                          .map((t) =>
                            t.carBodyOptions.length > 0
                              ? `${t.name}(${t.carBodyOptions.join("/")})`
                              : t.name,
                          )
                          .join(", ")}
                  </td>
                  <td className="px-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingDelete(spec);
                      }}
                      className="text-gray-400 transition-colors hover:text-red-600"
                    >
                      <HugeiconsIcon icon={Delete02Icon} size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <CarSpecDialog
        open={dialogState.open}
        companyGuid={companyGuid}
        spec={dialogState.open ? dialogState.spec : null}
        onClose={() => setDialogState({ open: false })}
      />

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingDelete?.carWeightStr} 삭제</AlertDialogTitle>
            <AlertDialogDescription>
              이 업체에서 더 이상 이 톤수를 선택할 수 없게 됩니다. 이미 등록된 화물과 단가에 남아
              있는 값은 그대로 유지됩니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-7 text-xs">취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) remove.mutate(pendingDelete.guid);
                setPendingDelete(null);
              }}
              className="h-7 bg-red-600 text-xs font-bold hover:bg-red-700"
            >
              삭제
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
